import { signal } from '@angular/core';

/**
 * R1 (TC-0009): how long a 429 asks the person to wait. Every API limiter reply carries the seconds twice — the body
 * field retryAfterSeconds and the Retry-After header (exposed through CORS) — and the network pause adds
 * code SIGNIN_PAUSED. The texts below use the API's own wording ("45 seconds", "2 minutes").
 */

/** Used when a 429 says nothing about the wait (e.g. one from a proxy). */
export const DEFAULT_WAIT_S = 30;
/** No on-screen wait is longer than an hour (the API's network pause is at most 30 minutes). */
export const MAX_WAIT_S = 3600;
/** The API's code for "too many wrong sign-ins from this network". Only login and forgot-password/verify send it. */
export const SIGNIN_PAUSED = 'SIGNIN_PAUSED';

/** 429s from these show their wait on their own page (sign-in, MFA, password reset, invite set-password) or are
 *  retried quietly (renewal), so the error interceptor shows no toast for them. Other /auth calls keep the toast. */
export const SELF_HANDLED_429_URL = /\/auth\/(login|verify-mfa|resend-mfa|mfa-setup\/(verify|email-code)|forgot-password(\/verify)?|set-password|refresh)(\?|#|$)/i;

/** Seconds from a 429: body.retryAfterSeconds, then the Retry-After header (seconds or an HTTP date); null when absent. */
export function retryAfterSeconds(err: unknown, nowMs: number = Date.now()): number | null {
  try {
    const e = err as any;
    const fromBody = Number(e?.error?.retryAfterSeconds);
    if (Number.isFinite(fromBody) && fromBody > 0) return Math.ceil(fromBody);
    const raw = e?.headers?.get?.('Retry-After');
    const h = raw == null ? '' : String(raw).trim();
    if (!h) return null;
    if (/^\d+$/.test(h)) return Number(h) > 0 ? Number(h) : null;
    const at = Date.parse(h);
    if (Number.isFinite(at)) { const n = Math.ceil((at - nowMs) / 1000); return n > 0 ? n : null; }
  } catch { /* fall through */ }
  return null;
}

/** The wait to show: the server's value (or `fallback`), rounded and kept within 1 s … MAX_WAIT_S. */
export function waitSeconds(err: unknown, fallback: number = DEFAULT_WAIT_S): number {
  return clampWait(retryAfterSeconds(err) ?? fallback);
}

export function clampWait(seconds: number): number {
  const s = Math.round(Number(seconds));
  return Number.isFinite(s) ? Math.min(Math.max(1, s), MAX_WAIT_S) : DEFAULT_WAIT_S;
}

/** The API's WaitText: "1 second", "45 seconds" (under 120), otherwise "N minutes" rounded up. */
export function waitText(seconds: number): string {
  const n = Math.max(1, Math.ceil(Number(seconds) || 0));
  if (n <= 1) return '1 second';
  if (n < 120) return `${n} seconds`;
  return `${Math.ceil(n / 60)} minutes`;
}

/** Short button label: "30s" under a minute, otherwise "N min" rounded up (the same rounding as the API). */
export function shortWait(seconds: number): string {
  const s = Math.max(0, Math.ceil(Number(seconds) || 0));
  return s < 60 ? `${s}s` : `${Math.ceil(s / 60)} min`;
}

/**
 * A seconds countdown for a "Try again in …" button. Deadline-based, so it stays right when a background tab
 * throttles timers. `left` is a signal, so the label updates even without zone change detection.
 * The owner must call stop() in ngOnDestroy.
 */
export class WaitCountdown {
  readonly left = signal(0);
  private timer: ReturnType<typeof setInterval> | null = null;
  private until = 0;

  start(seconds: number): void {
    this.stop();
    const s = clampWait(seconds);
    this.until = Date.now() + s * 1000;
    this.left.set(s);
    this.timer = setInterval(() => {
      const n = Math.max(0, Math.ceil((this.until - Date.now()) / 1000));
      this.left.set(n);
      if (n === 0) this.stop();
    }, 1000);
  }

  stop(): void {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }

  /** Stops and clears the wait (left = 0), e.g. when the screen that showed it is left. */
  reset(): void { this.stop(); this.left.set(0); }

  get running(): boolean { return this.timer !== null; }
}
